import { addPointModeAtom } from '@/atoms';
import { PointCloudOctree } from '@pnext/three-loader';
import { Text } from '@react-three/drei';
import { ThreeEvent, useThree } from '@react-three/fiber';
import { useAtomValue } from 'jotai';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import * as THREE from 'three';
import PointX from './PointX';

const MeasuringTool = ({
  origin,
  targets,
}: {
  origin: THREE.Vector3;
  targets: PointCloudOctree[];
  projectId?: string;
}) => {
  const [pickedPoints, setPickedPoints] = useState<THREE.Vector3[]>([]);
  const [hoverPoint, setHoverPoint] = useState<THREE.Vector3 | null>(null);
  const camera = useThree((state) => state.camera);
  const renderer = useThree((state) => state.gl);
  const raycaster = useMemo(() => new THREE.Raycaster(), []);
  const [params] = useSearchParams();
  const showMeasurements = params.get('showMeasurements') === 'true';
  const addPointMode = useAtomValue(addPointModeAtom);

  // refs for dragging state and group references (changed from sphere to group)
  const draggingRef = useRef<{
    index: number;
    plane: THREE.Plane;
    offset: THREE.Vector3;
  } | null>(null);

  // Hover handler - show preview marker
  useEffect(() => {
    if (!showMeasurements || addPointMode?.enabled) return;

    const handleMouseMove = (e: MouseEvent) => {
      // Calculate normalized device coordinates
      const rect = renderer.domElement.getBoundingClientRect();
      const x = ((e.clientX - rect.left) / rect.width) * 2 - 1;
      const y = -((e.clientY - rect.top) / rect.height) * 2 + 1;

      // Configure raycaster
      raycaster.setFromCamera(new THREE.Vector2(x, y), camera);
      const hits = targets.map((t) => t.pick(renderer, camera, raycaster.ray)).filter(Boolean);

      if (hits.length > 0 && hits[0]?.position) {
        setHoverPoint(hits[0].position.clone());
        renderer.domElement.style.cursor = 'crosshair';
      } else {
        setHoverPoint(null);
        renderer.domElement.style.cursor = 'default';
      }
    };

    renderer.domElement.addEventListener('pointermove', handleMouseMove);
    return () => {
      renderer.domElement.removeEventListener('pointermove', handleMouseMove);
      renderer.domElement.style.cursor = 'default';
    };
  }, [showMeasurements, addPointMode, targets, camera, renderer, raycaster]);

  useEffect(() => {
    if (!showMeasurements) return;
    const dom = renderer.domElement;

    const handlePointerUp = (e: MouseEvent) => {
      if (addPointMode?.enabled) return;
      if (draggingRef.current) return;

      const rect = dom.getBoundingClientRect();
      const x = ((e.clientX - rect.left) / rect.width) * 2 - 1;
      const y = -((e.clientY - rect.top) / rect.height) * 2 + 1;

      raycaster.setFromCamera(new THREE.Vector2(x, y), camera);
      const hits = targets.map((t) => t.pick(renderer, camera, raycaster.ray)).filter(Boolean);

      if (hits.length === 0 || !hits[0]?.position) return;

      const world = new THREE.Vector3(...hits[0].position.toArray());
      setPickedPoints((prev) => {
        const pts = prev.length === 2 ? [] : prev;
        return [...pts, world];
      });

      // Clear hover point when a point is picked
      setHoverPoint(null);
    };

    renderer.domElement.addEventListener('pointerup', handlePointerUp);
    return () => renderer.domElement.removeEventListener('pointerup', handlePointerUp);
  }, [targets, camera, renderer, raycaster, addPointMode, showMeasurements]);

  useEffect(() => {
    if (!showMeasurements) {
      setPickedPoints([]);
      setHoverPoint(null);
    }
  }, [showMeasurements]);

  const startDrag = (e: ThreeEvent<PointerEvent>, index: number) => {
    e.stopPropagation();
    const point = e.point.clone();
    const camDir = new THREE.Vector3().copy(camera.getWorldDirection(new THREE.Vector3()));
    const plane = new THREE.Plane().setFromNormalAndCoplanarPoint(camDir, point);
    const offset = point.clone().sub(pickedPoints[index]);
    draggingRef.current = { index, plane, offset };

    const dom = renderer.domElement;

    const onPointerMove = (ev: PointerEvent) => {
      ev.stopPropagation();
      const rect = dom.getBoundingClientRect();
      const x = ((ev.clientX - rect.left) / rect.width) * 2 - 1;
      const y = -((ev.clientY - rect.top) / rect.height) * 2 + 1;
      raycaster.setFromCamera(new THREE.Vector2(x, y), camera);
      const intersectPoint = new THREE.Vector3();
      if (raycaster.ray.intersectPlane(plane, intersectPoint)) {
        setPickedPoints((prev) => {
          const pts = [...prev];
          pts[index] = intersectPoint.clone().sub(offset);
          return pts;
        });
      }
    };

    const onPointerUp = () => {
      window.removeEventListener('pointermove', onPointerMove, true);
      window.removeEventListener('pointerup', onPointerUp);

      const hits = targets.map((t) => t.pick(renderer, camera, raycaster.ray)).filter(Boolean);

      if (hits.length > 0 && hits[0]?.position) {
        const world = new THREE.Vector3(...hits[0].position.toArray());
        setPickedPoints((prev) => {
          const pts = [...prev];
          pts[index] = world;
          return pts;
        });
      }

      draggingRef.current = null;
    };

    window.addEventListener('pointermove', onPointerMove, true);
    window.addEventListener('pointerup', onPointerUp);
  };

  const relativePoints = useMemo(() => pickedPoints.map((p) => p.clone().sub(origin)), [pickedPoints, origin]);

  // Create triangle points for height visualization
  const trianglePoints = useMemo(() => {
    if (pickedPoints.length < 2) return [];

    // Use the lower z value as the base
    const baseZ = Math.min(pickedPoints[0].z, pickedPoints[1].z);
    const lowerPoint = pickedPoints[0].z < pickedPoints[1].z ? pickedPoints[0] : pickedPoints[1];
    const higherPoint = pickedPoints[0].z < pickedPoints[1].z ? pickedPoints[1] : pickedPoints[0];

    // Create a corner point at the same x,y as higher point but z of lower point
    const cornerPoint = new THREE.Vector3(higherPoint.x, higherPoint.y, baseZ);

    return [lowerPoint.clone().sub(origin), cornerPoint.clone().sub(origin), higherPoint.clone().sub(origin)];
  }, [pickedPoints, origin]);

  const heightLinePoints = useMemo(() => {
    if (pickedPoints.length < 2) return [];
    const baseZ = Math.min(pickedPoints[0].z, pickedPoints[1].z);
    const higherPoint = pickedPoints[0].z < pickedPoints[1].z ? pickedPoints[1] : pickedPoints[0];
    const cornerPoint = new THREE.Vector3(higherPoint.x, higherPoint.y, baseZ);
    return [cornerPoint.clone().sub(origin), higherPoint.clone().sub(origin)];
  }, [pickedPoints, origin]);

  const textPosition = useMemo(() => {
    if (pickedPoints.length < 2) return new THREE.Vector3();
    return pickedPoints[0].clone().add(pickedPoints[1]).multiplyScalar(0.5);
  }, [pickedPoints]);

  const fontSize = useMemo(() => {
    if (pickedPoints.length < 2) return 0;
    return textPosition.distanceTo(camera.position) / 50;
  }, [textPosition, pickedPoints, camera]);

  const heightTextPosition = useMemo(() => {
    if (pickedPoints.length < 2) return new THREE.Vector3();
    const baseZ = Math.min(pickedPoints[0].z, pickedPoints[1].z);
    const higherPoint = pickedPoints[0].z < pickedPoints[1].z ? pickedPoints[1] : pickedPoints[0];
    const cornerPoint = new THREE.Vector3(higherPoint.x, higherPoint.y, baseZ);
    return cornerPoint.clone().add(higherPoint).multiplyScalar(0.5);
  }, [pickedPoints]);

  const heightDifference = useMemo(() => {
    if (pickedPoints.length < 2) return 0;
    return Math.abs(pickedPoints[1].z - pickedPoints[0].z);
  }, [pickedPoints]);

  const showHeightVisualization = heightDifference >= 0.01;

  const labelsAreTooClose = useMemo(() => {
    if (pickedPoints.length < 2 || !showHeightVisualization) return false;
    return textPosition.distanceTo(heightTextPosition) < fontSize * 5;
  }, [textPosition, heightTextPosition, fontSize, pickedPoints, showHeightVisualization]);

  const calculateScale = (point: THREE.Vector3) => {
    const d = camera.position.distanceTo(point);
    const pr = renderer.getPixelRatio();
    const height = renderer.domElement.clientHeight * pr;

    if (camera.type === 'OrthographicCamera') {
      const orthographicCamera = camera as THREE.OrthographicCamera;
      const worldScreenHeight = Math.abs(orthographicCamera.top - orthographicCamera.bottom);
      const unitsPerPixel = worldScreenHeight / height;
      return 20 * unitsPerPixel;
    } else {
      const perspectiveCamera = camera as THREE.PerspectiveCamera;
      const vFOV = (perspectiveCamera.getEffectiveFOV() * Math.PI) / 180;
      const worldScreenHeight = 2 * d * Math.tan(vFOV / 2);
      const unitsPerPixel = worldScreenHeight / height;
      return 20 * unitsPerPixel;
    }
  };

  const scales = useMemo(() => pickedPoints.map(calculateScale), [pickedPoints, camera, renderer]);

  const hoverScale = useMemo(() => (hoverPoint ? calculateScale(hoverPoint) : 1), [hoverPoint, camera, renderer]);

  const textRotation = useMemo(() => {
    if (camera.type === 'OrthographicCamera') return new THREE.Quaternion();
    const quaternion = new THREE.Quaternion();
    quaternion.copy(camera.quaternion);

    return quaternion;
  }, [textPosition, camera]);

  if (!showMeasurements) return null;

  return (
    <>
      {/* Hover preview marker - semi-transparent */}
      {hoverPoint && !draggingRef.current && <PointX scale={hoverScale} position={hoverPoint} />}

      {pickedPoints.map((point, i) => (
        <PointX key={i} scale={scales[i]} position={point} onPointerDown={(e) => startDrag(e, i)} />
      ))}

      {pickedPoints.length === 2 && (
        <>
          {/* Main distance line */}
          <line renderOrder={Number.MAX_SAFE_INTEGER} position={origin.toArray()}>
            <bufferGeometry attach='geometry' {...new THREE.BufferGeometry().setFromPoints(relativePoints)} />
            <lineBasicMaterial transparent opacity={0.8} depthTest={false} attach='material' color='red' />
          </line>

          {showHeightVisualization && (
            <>
              {/* Triangle visualization for height */}
              <line renderOrder={Number.MAX_SAFE_INTEGER} position={origin.toArray()}>
                <bufferGeometry attach='geometry' {...new THREE.BufferGeometry().setFromPoints(trianglePoints)} />
                <lineBasicMaterial transparent opacity={0.5} depthTest={false} attach='material' color='blue' />
              </line>

              {/* Vertical height line */}
              <line renderOrder={Number.MAX_SAFE_INTEGER} position={origin.toArray()}>
                <bufferGeometry attach='geometry' {...new THREE.BufferGeometry().setFromPoints(heightLinePoints)} />
                <lineBasicMaterial transparent opacity={0.8} depthTest={false} attach='material' color='green' />
              </line>
            </>
          )}

          {!draggingRef.current ? (
            labelsAreTooClose ? (
              <Text
                position={textPosition}
                fontSize={fontSize}
                color='white'
                outlineColor='black'
                outlineWidth={0.01}
                outlineBlur={0.01}
                strokeColor='red'
                strokeWidth={0.001}
                depthOffset={Number.MIN_SAFE_INTEGER}
                renderOrder={Number.MAX_SAFE_INTEGER}
                anchorX='center'
                anchorY='middle'
                quaternion={textRotation}>
                D{pickedPoints[0].distanceTo(pickedPoints[1]).toFixed(2)} | H{heightDifference.toFixed(2)}
              </Text>
            ) : (
              <>
                <Text
                  position={textPosition}
                  fontSize={fontSize}
                  color='white'
                  outlineColor='black'
                  outlineWidth={0.01}
                  outlineBlur={0.01}
                  strokeColor='red'
                  strokeWidth={0.001}
                  depthOffset={Number.MIN_SAFE_INTEGER}
                  renderOrder={Number.MAX_SAFE_INTEGER}
                  anchorX='center'
                  anchorY='middle'
                  quaternion={textRotation}>
                  D{pickedPoints[0].distanceTo(pickedPoints[1]).toFixed(2)}
                </Text>
                {showHeightVisualization && (
                  <Text
                    position={heightTextPosition}
                    fontSize={fontSize}
                    color='white'
                    outlineColor='black'
                    outlineWidth={0.01}
                    outlineBlur={0.01}
                    strokeColor='green'
                    strokeWidth={0.001}
                    depthOffset={Number.MIN_SAFE_INTEGER}
                    renderOrder={Number.MAX_SAFE_INTEGER}
                    anchorX='center'
                    anchorY='middle'
                    quaternion={textRotation}>
                    H{heightDifference.toFixed(2)}
                  </Text>
                )}
              </>
            )
          ) : null}
        </>
      )}
    </>
  );
};

export default MeasuringTool;
